import React, { VFC, useEffect, useMemo } from 'react';
import { StepEditorProps } from 'src/editor/step-editor';
import { useEditorContext } from 'src/store/editor';
import { apiGetJourney, apiPostJourneyInteractionOpen } from 'src/utils/journeyApi';
import axios from 'axios';

import { ContentCreationEditor } from 'src/editor/content-creation/editor';
import { useEditorStore } from 'src/editor/content-creation/editor-store';
import { setEmbeddedMode } from 'src/editor/content-creation/editor-sync';
import { getJourneyThemeSettings } from 'src/editor/content-creation/helpers/get-journey-theme-settings';
import { useCurrentOrganization } from 'src/store/organization';
import { useFeatureFlag } from 'src/editor/content-creation/feature-flags-store';
import { functionNoop } from 'src/utils/function/noop';
import { journeyToSections } from 'src/editor/content-creation/helpers/transforms/journey-to-sections';
import { getIdFromFriendlySectionPath } from 'src/editor/content-creation/helpers/section/get-id-from-friendly-section-path';
import { usePlayerStore } from 'src/editor/content-creation/neue-player/player-store';
import { useCurrentUser } from 'src/store/user';
import { useTalkToJourneyStore } from 'src/editor/content-creation/talk-to-journey/store';
import { useClipboardStore } from 'src/editor/content-creation/utils/clipboard.store';
import { shallow } from 'zustand/shallow';
import { loadAllFontFamilies } from 'src/document-editor/font-utils';
import { getFirstSection } from 'src/editor/content-creation/helpers/section/find-section-by-id';
import { getAliasAnnotation } from 'src/editor/content-creation/helpers/transforms/get-alias-annotation';
import { converAnnotationToForm } from 'src/common/helpers/annotation';
import JoyrideOnboardingGuide from 'src/onboarding/joyride-onboarding';
import { isCustomFontStrictlyUsedForOrg, useJourneyDynamicFontStore } from 'src/store/journey-dynamic-font';
import { appendDynamicFont, applyStrictCustomFont } from 'src/utils/dynamic-fonts';

interface PageJourneysContentCreationEditMobileProps extends Pick<StepEditorProps, 'nodeId'> {
  uuid: string;
  sectionPath?: string;
}

let originalConsoleLog = console.log;

// ============================================================================
// STATIC DATA CONFIGURATION
// ============================================================================
/**
 * WORKFLOW TO REPLACE API CALLS WITH STATIC DATA:
 * 
 * Step 1: Capture API Responses
 *   - Set USE_API_CALLS = true (default)
 *   - Run the application and navigate to a journey
 *   - Open browser console (F12)
 *   - Look for "API RESPONSE CAPTURE" logs
 *   - Copy the response data from console
 *   - Responses are also saved to localStorage (access via: localStorage.getItem("api_response_captures"))
 * 
 * Step 2: Populate Static Data
 *   - Copy the journey response to STATIC_JOURNEY_DATA object (keyed by UUID)
 *   - Copy the interaction response to STATIC_INTERACTION_RESPONSE
 *   - Copy the user response to STATIC_USER_DATA
 *   - Copy the organization response to STATIC_ORGANIZATION_DATA
 * 
 * Step 3: Switch to Static Data
 *   - Set USE_API_CALLS = false
 *   - The application will now use static data instead of making API calls
 *   - User and organization data will also be captured and can be replaced with static data
 * 
 * NOTE: The capture mechanism logs responses in a format that's ready to copy-paste
 *       into the static data objects below.
 */
const USE_API_CALLS = false; // Changed to false - now using static data

// Captured journey data from API response
// UUID: cfac29fd696242ec9f316e7e2d9b8b2a
// This data was captured from the actual API response and is used when USE_API_CALLS is false
const CAPTURED_JOURNEY_DATA = {
  "aliases": [
    {
      "allow_comments": true,
      "allowed_emails": null,
      "blacklisted_emails": null,
      "company_name": null,
      "contact_email": null,
      "contact_first_name": null,
      "contact_last_name": null,
      "custom_fields": [],
      "custom_nda_template_url": null,
      "disable_indexing": true,
      "email_param_enabled": false,
      "email_required": false,
      "email_verification": false,
      "enabled": true,
      "expires_at": null,
      "has_password": false,
      "hubspot_sync": false,
      "id": 1,
      "intro_video_asset": null,
      "is_primary": true,
      "is_subscribed_to_comments_status": "disabled",
      "journey_id": 1,
      "name": "Primary Link",
      "nda_legal_entity_name": "Famey",
      "nda_required": false,
      "personalization": null,
      "presentation_mode": true,
      "slug": "cfac29fd696242ec9f316e7e2d9b8b2a",
      "uuid": "56b75ce581684beeac6237e9fa9d9126"
    }
  ],
  "branding": {
    "custom_meta_description": null,
    "custom_title": null,
    "force_generated_colors": false,
    "heading_typeface": "serif",
    "hide_journey_upsell": false,
    "icon_url": null,
    "is_applied": true,
    "journey_thumbnail_url": "https://assets-raw.jny.io/thumbnails/ce08c03c742b4bcfaa403f14fecdf1d8.png",
    "logo_url": null,
    "primary_color": null,
    "secondary_color": null,
    "thumbnail_style": "image"
  },
  "created_at": "2025-11-04T09:20:48.002560",
  "creator": {
    "avatar_url": null,
    "email": "goutham@famey.io",
    "id": 1,
    "name": "goutham@famey.io",
    "uuid": "59546d0594d34e15bf4e00b6058ca4f6"
  },
  "cta": null,
  "data": {
    "neue": true,
    "talk_to_journey": {
      "disabled": true,
      "shouldGreetVisitors": false
    }
  },
  "engagements": {},
  "feature_flags": {},
  "id": 1,
  "is_folders_enabled": false,
  "is_followed": true,
  "is_gallery_mode_enabled": false,
  "is_neue": true,
  "is_onboarding_journey": false,
  "is_session_followed": false,
  "is_template": false,
  "name": "Welcome to Journey",
  "num_of_steps": 3,
  "organization": {
    "custom_domain": null,
    "name": "Famey",
    "slug": "famey",
    "url": "https://famey.io"
  },
  "prospect_personalization": null,
  "signature_record": null,
  "slug": "cfac29fd696242ec9f316e7e2d9b8b2a",
  "social_sharing": true,
  "status": "public",
  "steps": [
    {
      "id": 1,
      "journey_id": 1,
      "name": null,
      "nodes": [
        {
          "annotations": [],
          "content_id": null,
          "content_name": "",
          "content_type": "placeholder",
          "content_uuid": null,
          "creator": {
            "avatar_url": null,
            "email": "goutham@famey.io",
            "name": "goutham@famey.io"
          },
          "data": null,
          "document": null,
          "embed": null,
          "engagements": {},
          "external_integration": null,
          "external_link": null,
          "file_asset": null,
          "folder_key": null,
          "folder_name": null,
          "friendly_path": "welcome-to-journey-fb47d5fa",
          "guidance_details": {},
          "hidden": false,
          "id": 1,
          "image_asset": null,
          "image_collection": null,
          "integration": null,
          "is_email_required": false,
          "is_placeholder": false,
          "is_synced": false,
          "name": "Welcome to Journey",
          "name_set_manually": false,
          "node_blocks": [
            {
              "block": {
                "content": {
                  "contentUUID": null,
                  "horizontalAlignment": "center",
                  "type": "text",
                  "value": "<h1 class=\"nodeview-heading \" style=\"text-align: center\"><span style=\"color: rgb(26, 31, 113)\">Welcome to Journey</span></h1>",
                  "valueJSON": {
                    "content": [
                      {
                        "attrs": {
                          "huge": false,
                          "level": 1,
                          "textAlign": "center"
                        },
                        "content": [
                          {
                            "marks": [
                              {
                                "attrs": {
                                  "color": "rgb(26, 31, 113)",
                                  "fontFamily": null,
                                  "fontWeight": null
                                },
                                "type": "textStyle"
                              }
                            ],
                            "text": "Welcome to Journey",
                            "type": "text"
                          }
                        ],
                        "type": "heading"
                      }
                    ],
                    "type": "doc"
                  }
                },
                "id": "4f1fd0f7df624794a60fdf6b08e6f0b0",
                "layout": {
                  "spec": {
                    "height": null,
                    "type": "calculated-height",
                    "width": "100%"
                  },
                  "verticalAlign": "center"
                },
                "position": "a1"
              },
              "block_uuid": "4f1fd0f7df624794a60fdf6b08e6f0b0",
              "content_uuid": null,
              "created_at": "Tue, 04 Nov 2025 09:20:48 GMT",
              "id": 1,
              "node_id": 1,
              "position": "a1",
              "updated_at": "Wed, 03 Dec 2025 17:58:09 GMT"
            },
            {
              "block": {
                "content": {
                  "contentUUID": null,
                  "horizontalAlignment": "left",
                  "type": "text",
                  "value": "<p class=\"nodeview-paragraph\">Create personalized buyer microsites that consolidate all your sales content into a single, branded link. No coding required.</p>",
                  "valueJSON": {
                    "content": [
                      {
                        "attrs": {
                          "textAlign": "left"
                        },
                        "content": [
                          {
                            "text": "Create personalized buyer microsites that consolidate all your sales content into a single, branded link. No coding required.",
                            "type": "text"
                          }
                        ],
                        "type": "paragraph"
                      }
                    ],
                    "type": "doc"
                  }
                },
                "id": "defc112d9cdd4147868b40ecc51850bb",
                "layout": {
                  "spec": {
                    "height": null,
                    "type": "calculated-height",
                    "width": "100%"
                  },
                  "verticalAlign": "center"
                },
                "position": "a3"
              },
              "block_uuid": "defc112d9cdd4147868b40ecc51850bb",
              "content_uuid": null,
              "created_at": "Wed, 12 Nov 2025 05:32:25 GMT",
              "id": 83,
              "node_id": 1,
              "position": "a3",
              "updated_at": "Wed, 03 Dec 2025 18:25:17 GMT"
            },
            {
              "block": {
                "content": {
                  "backgroundColor": "#172c97",
                  "contentUUID": null,
                  "target": "https://app.journey.io",
                  "text": "Get Started with Journey",
                  "textColor": "#FFFFFF",
                  "type": "cta-button"
                },
                "id": "12570fe6bc2f4c918611bc4f623e1c32",
                "layout": {
                  "spec": {
                    "height": null,
                    "type": "calculated-height",
                    "width": "100%"
                  },
                  "verticalAlign": "top"
                },
                "position": "a4"
              },
              "block_uuid": "12570fe6bc2f4c918611bc4f623e1c32",
              "content_uuid": null,
              "created_at": "Wed, 03 Dec 2025 18:02:17 GMT",
              "id": 118,
              "node_id": 1,
              "position": "a4",
              "updated_at": "Wed, 03 Dec 2025 18:25:43 GMT"
            },
            {
              "block": {
                "content": {
                  "contentObject": {
                    "file_content_type": "image",
                    "file_url": "blob:http://app.journey.qqq/fa0f253f-1ce3-4932-8e24-21c73cebb5a1",
                    "name": "clipboard-image"
                  },
                  "dominantColor": "#fbfbfb",
                  "fileUploadProgress": 100,
                  "fileUploadStatus": "complete",
                  "objectFit": "contain",
                  "personalContent": true,
                  "staticShow": true,
                  "type": "image",
                  "url": "https://devuploadsbucket-0a4277ce.s3.amazonaws.com/neue-test/2270e6beafad4187878aae852dfb3a56.png"
                },
                "id": "dcf38a8389ec4b1f8ae50402ebc1df4c",
                "layout": {
                  "spec": {
                    "aspectRatio": {
                      "height": 272,
                      "width": 692
                    },
                    "height": null,
                    "type": "auto-height",
                    "width": "100%"
                  },
                  "verticalAlign": "center"
                },
                "position": "a6"
              },
              "block_uuid": "dcf38a8389ec4b1f8ae50402ebc1df4c",
              "content_uuid": null,
              "created_at": "Wed, 03 Dec 2025 20:12:45 GMT",
              "id": 198,
              "node_id": 1,
              "position": "a6",
              "updated_at": "Wed, 03 Dec 2025 20:12:49 GMT"
            }
          ],
          "parent_node_id": null,
          "slug": null,
          "step_node_id": 1,
          "synced_name": null,
          "text": null,
          "thumbnail": null,
          "type": {
            "data": null,
            "friendly_name": "Neue",
            "id": 12,
            "name": "neue"
          },
          "updated_at": "2025-12-03T17:58:09.565782",
          "user_id": 1,
          "user_prospect": null,
          "uuid": "fb47d5fa1b50491cb954583e2dc23927",
          "video_asset": null
        },
        {
          "annotations": [],
          "content_id": null,
          "content_name": "",
          "content_type": "placeholder",
          "content_uuid": null,
          "creator": {
            "avatar_url": null,
            "email": "goutham@famey.io",
            "name": "goutham@famey.io"
          },
          "data": null,
          "document": null,
          "embed": null,
          "engagements": {},
          "external_integration": null,
          "external_link": null,
          "file_asset": null,
          "folder_key": null,
          "folder_name": null,
          "friendly_path": "seconds-4a6cdb6c",
          "guidance_details": {},
          "hidden": false,
          "id": 3,
          "image_asset": null,
          "image_collection": null,
          "integration": null,
          "is_email_required": false,
          "is_placeholder": false,
          "is_synced": false,
          "name": "Details about Journey",
          "name_set_manually": true,
          "node_blocks": [
            {
              "block": {
                "content": {
                  "contentUUID": null,
                  "horizontalAlignment": "left",
                  "type": "text",
                  "value": "<p class=\"nodeview-paragraph\">Journey is the buyer microsite tool designed for B2B sales teams, founders, and marketers. Create personalized, interactive microsites that consolidate all your sales content—decks, PDFs, videos, demos, and more—into a single, branded link that buyers can access from any device.</p><p class=\"nodeview-paragraph\" style=\"text-align: start\">With Journey's no-code builder, you can easily incorporate existing assets into a cohesive, scrollable microsite without any design expertise. Personalize each Journey with client-specific information including logos, use cases, team introductions, and pricing details to create a customized experience at scale.</p><p class=\"nodeview-paragraph\" style=\"text-align: start\">Monitor buyer interactions with detailed analytics including time spent on pages, scroll depth, and click-through rates. Choose to gate content by requiring emails or passcodes, or provide open access depending on your engagement strategy. Journey seamlessly integrates with CRMs like Salesforce and HubSpot, as well as tools such as Gong, Loom, Figma, and Calendly.</p><p class=\"nodeview-paragraph\" style=\"text-align: start\"><strong>Key Features</strong></p><ul><li><div></div><div><p class=\"nodeview-paragraph\">Content Integration: Easily incorporate decks, PDFs, videos, and demos into a cohesive microsite</p></div></li><li><div></div><div><p class=\"nodeview-paragraph\">Personalization: Tailor each Journey with client-specific information at scale</p></div></li><li><div></div><div><p class=\"nodeview-paragraph\">Access Control: Gate content with emails, passcodes, or provide open access</p></div></li><li><div></div><div><p class=\"nodeview-paragraph\">Analytics &amp; Tracking: Monitor buyer interactions with detailed insights</p></div></li><li><div></div><div><p class=\"nodeview-paragraph\">No-Code Builder: Drag-and-drop interface to create visually engaging sales journeys</p></div></li><li><div></div><div><p class=\"nodeview-paragraph\">Templates &amp; Branding: Access reusable templates and brand kits for consistency</p></div></li><li><div></div><div><p class=\"nodeview-paragraph\">CRM Integrations: Connect with Salesforce, HubSpot, and other tools</p></div></li><li><div></div><div><p class=\"nodeview-paragraph\">Mobile Responsive: Ensure buyers can engage from any device without friction</p></div></li></ul><p class=\"nodeview-paragraph\" style=\"text-align: start\">By consolidating scattered sales materials into a single, interactive microsite, Journey improves buyer engagement, streamlines the sales process, and increases deal closure rates for B2B teams.</p>",
                  "valueJSON": {
                    "content": [
                      {
                        "attrs": {
                          "textAlign": "left"
                        },
                        "content": [
                          {
                            "text": "Journey is the buyer microsite tool designed for B2B sales teams, founders, and marketers. Create personalized, interactive microsites that consolidate all your sales content—decks, PDFs, videos, demos, and more—into a single, branded link that buyers can access from any device.",
                            "type": "text"
                          }
                        ],
                        "type": "paragraph"
                      },
                      {
                        "attrs": {
                          "textAlign": "start"
                        },
                        "content": [
                          {
                            "text": "With Journey's no-code builder, you can easily incorporate existing assets into a cohesive, scrollable microsite without any design expertise. Personalize each Journey with client-specific information including logos, use cases, team introductions, and pricing details to create a customized experience at scale.",
                            "type": "text"
                          }
                        ],
                        "type": "paragraph"
                      },
                      {
                        "attrs": {
                          "textAlign": "start"
                        },
                        "content": [
                          {
                            "text": "Monitor buyer interactions with detailed analytics including time spent on pages, scroll depth, and click-through rates. Choose to gate content by requiring emails or passcodes, or provide open access depending on your engagement strategy. Journey seamlessly integrates with CRMs like Salesforce and HubSpot, as well as tools such as Gong, Loom, Figma, and Calendly.",
                            "type": "text"
                          }
                        ],
                        "type": "paragraph"
                      },
                      {
                        "attrs": {
                          "textAlign": "start"
                        },
                        "content": [
                          {
                            "marks": [
                              {
                                "type": "bold"
                              }
                            ],
                            "text": "Key Features",
                            "type": "text"
                          }
                        ],
                        "type": "paragraph"
                      },
                      {
                        "content": [
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Content Integration: Easily incorporate decks, PDFs, videos, and demos into a cohesive microsite",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          },
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Personalization: Tailor each Journey with client-specific information at scale",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          },
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Access Control: Gate content with emails, passcodes, or provide open access",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          },
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Analytics & Tracking: Monitor buyer interactions with detailed insights",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          },
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "No-Code Builder: Drag-and-drop interface to create visually engaging sales journeys",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          },
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Templates & Branding: Access reusable templates and brand kits for consistency",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          },
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "CRM Integrations: Connect with Salesforce, HubSpot, and other tools",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          },
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Mobile Responsive: Ensure buyers can engage from any device without friction",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          }
                        ],
                        "type": "bulletList"
                      },
                      {
                        "attrs": {
                          "textAlign": "start"
                        },
                        "content": [
                          {
                            "text": "By consolidating scattered sales materials into a single, interactive microsite, Journey improves buyer engagement, streamlines the sales process, and increases deal closure rates for B2B teams.",
                            "type": "text"
                          }
                        ],
                        "type": "paragraph"
                      }
                    ],
                    "type": "doc"
                  }
                },
                "id": "3ed1974696df4f48b362b0a943464030",
                "layout": {
                  "spec": {
                    "height": null,
                    "type": "calculated-height",
                    "width": "100%"
                  },
                  "verticalAlign": "center"
                },
                "position": "a2"
              },
              "block_uuid": "3ed1974696df4f48b362b0a943464030",
              "content_uuid": null,
              "created_at": "Wed, 12 Nov 2025 05:38:35 GMT",
              "id": 92,
              "node_id": 3,
              "position": "a2",
              "updated_at": "Wed, 03 Dec 2025 20:17:50 GMT"
            },
            {
              "block": {
                "content": {
                  "contentUUID": null,
                  "horizontalAlignment": "left",
                  "type": "text",
                  "value": "<h2 class=\"nodeview-heading \"><strong>Transform Your Sales Process</strong></h2>",
                  "valueJSON": {
                    "content": [
                      {
                        "attrs": {
                          "huge": false,
                          "level": 2,
                          "textAlign": "left"
                        },
                        "content": [
                          {
                            "marks": [
                              {
                                "type": "bold"
                              }
                            ],
                            "text": "Transform Your Sales Process",
                            "type": "text"
                          }
                        ],
                        "type": "heading"
                      }
                    ],
                    "type": "doc"
                  }
                },
                "id": "3b823ffaa7a84450bb60e7d108062f72",
                "layout": {
                  "spec": {
                    "height": 83,
                    "type": "calculated-height",
                    "width": "100%"
                  },
                  "verticalAlign": "center"
                },
                "position": "a0"
              },
              "block_uuid": "3b823ffaa7a84450bb60e7d108062f72",
              "content_uuid": null,
              "created_at": "Wed, 12 Nov 2025 05:33:31 GMT",
              "id": 87,
              "node_id": 3,
              "position": "a0",
              "updated_at": "Wed, 03 Dec 2025 18:28:15 GMT"
            },
            {
              "block": {
                "content": {
                  "contentType": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                  "contentUUID": "a6012fa9159c4478bca0a7ce1e9a305b",
                  "file": {
                    "path": "June 26 - Journey for Customer Success.docx"
                  },
                  "fileUploadProgress": 100,
                  "fileUploadStatus": "complete",
                  "jnyContent": {
                    "content_type": "file",
                    "created_at": "2025-12-03T18:25:29.498841",
                    "creator": {
                      "email": "goutham@famey.io",
                      "name": "goutham@famey.io"
                    },
                    "document": null,
                    "embed": null,
                    "external_link": null,
                    "file_asset": {
                      "content_type": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                      "data": null,
                      "id": 1,
                      "image_collection": null,
                      "metadata": null,
                      "name": "June 26 - Journey for Customer Success.docx",
                      "properties": null,
                      "url": "https://d1r6rl8kmso535.cloudfront.net/file-assets/0958a01da0714236a7361ba235a6994e.docx"
                    },
                    "id": 2,
                    "image_asset": null,
                    "name": "June 26 - Journey for Customer Success.docx",
                    "should_show_library": null,
                    "tags": [],
                    "thumbnail": null,
                    "updated_at": "2025-12-03T18:25:29.502052",
                    "uuid": "a6012fa9159c4478bca0a7ce1e9a305b",
                    "video_asset": null
                  },
                  "name": "June 26 - Journey for Customer Success.docx",
                  "type": "ms-office",
                  "url": "https://devuploadsbucket-0a4277ce.s3.amazonaws.com/file-assets/0958a01da0714236a7361ba235a6994e.docx"
                },
                "id": "da50c45adc6f468eb394143e139132f3",
                "layout": {
                  "spec": {
                    "height": 750,
                    "type": "calculated-height",
                    "width": "100%"
                  },
                  "verticalAlign": "top"
                },
                "position": "Zz"
              },
              "block_uuid": "da50c45adc6f468eb394143e139132f3",
              "content_uuid": "a6012fa9-159c-4478-bca0-a7ce1e9a305b",
              "created_at": "Wed, 03 Dec 2025 18:25:13 GMT",
              "id": 157,
              "node_id": 3,
              "position": "Zz",
              "updated_at": "Wed, 03 Dec 2025 18:27:41 GMT"
            },
            {
              "block": {
                "content": {
                  "contentObject": {
                    "link_url": "https://stupendous-alfajores-e09f2b.netlify.app/"
                  },
                  "displayOption": "inline",
                  "link": {
                    "destination": {
                      "host": "netlify.app",
                      "logo_url": null
                    },
                    "iframe_safe": true,
                    "third_party_iframe_safe": true,
                    "thumbnail": {
                      "height": null,
                      "url": null,
                      "width": null
                    },
                    "transformed_url": "https://stupendous-alfajores-e09f2b.netlify.app/",
                    "url": "https://stupendous-alfajores-e09f2b.netlify.app/"
                  },
                  "personalContent": true,
                  "staticShow": true,
                  "type": "link"
                },
                "id": "05f8b0d0be6f451aa7dc6f7c40cf124d",
                "layout": {
                  "spec": {
                    "height": 938,
                    "type": "calculated-height",
                    "width": "100%"
                  },
                  "verticalAlign": "top"
                },
                "position": "a4"
              },
              "block_uuid": "05f8b0d0be6f451aa7dc6f7c40cf124d",
              "content_uuid": null,
              "created_at": "Wed, 03 Dec 2025 20:17:38 GMT",
              "id": 203,
              "node_id": 3,
              "position": "a4",
              "updated_at": "Wed, 03 Dec 2025 20:17:59 GMT"
            }
          ],
          "parent_node_id": null,
          "slug": null,
          "step_node_id": 3,
          "synced_name": null,
          "text": null,
          "thumbnail": null,
          "type": {
            "data": null,
            "friendly_name": "Neue",
            "id": 12,
            "name": "neue"
          },
          "updated_at": "2025-11-12T05:33:31.329474",
          "user_id": 1,
          "user_prospect": null,
          "uuid": "4a6cdb6c61594a559fd926f6431969c5",
          "video_asset": null
        },
        {
          "annotations": [],
          "content_id": null,
          "content_name": "",
          "content_type": "placeholder",
          "content_uuid": null,
          "creator": {
            "avatar_url": null,
            "email": "goutham@famey.io",
            "name": "goutham@famey.io"
          },
          "data": null,
          "document": null,
          "embed": null,
          "engagements": {},
          "external_integration": null,
          "external_link": null,
          "file_asset": null,
          "folder_key": null,
          "folder_name": null,
          "friendly_path": "thirds-1297d720",
          "guidance_details": {},
          "hidden": false,
          "id": 4,
          "image_asset": null,
          "image_collection": null,
          "integration": null,
          "is_email_required": false,
          "is_placeholder": false,
          "is_synced": false,
          "name": "Why Choose Journey",
          "name_set_manually": true,
          "node_blocks": [
            {
              "block": {
                "content": {
                  "contentObject": {
                    "link_url": "https://calendly.com/journey-danny/call"
                  },
                  "contentUUID": null,
                  "displayOption": "inline",
                  "link": {
                    "destination": {
                      "host": "calendly.com",
                      "logo_url": null
                    },
                    "iframe_safe": true,
                    "third_party_iframe_safe": true,
                    "thumbnail": {
                      "height": null,
                      "url": null,
                      "width": null
                    },
                    "transformed_url": "https://calendly.com/journey-danny/call",
                    "url": "https://calendly.com/journey-danny/call"
                  },
                  "personalContent": true,
                  "staticShow": true,
                  "type": "link"
                },
                "id": "f4add537fb67436a913ba256d4b6e23e",
                "layout": {
                  "spec": {
                    "height": 673,
                    "type": "calculated-height",
                    "width": "100%"
                  },
                  "verticalAlign": "top"
                },
                "position": "a2"
              },
              "block_uuid": "f4add537fb67436a913ba256d4b6e23e",
              "content_uuid": null,
              "created_at": "Wed, 03 Dec 2025 18:07:17 GMT",
              "id": 149,
              "node_id": 4,
              "position": "a2",
              "updated_at": "Wed, 03 Dec 2025 20:30:25 GMT"
            },
            {
              "block": {
                "content": {
                  "contentUUID": null,
                  "horizontalAlignment": "left",
                  "type": "text",
                  "value": "<p class=\"nodeview-paragraph\">Get started with Journey today and experience the future of B2B sales presentations.</p>",
                  "valueJSON": {
                    "content": [
                      {
                        "attrs": {
                          "textAlign": "left"
                        },
                        "content": [
                          {
                            "text": "Get started with Journey today and experience the future of B2B sales presentations.",
                            "type": "text"
                          }
                        ],
                        "type": "paragraph"
                      }
                    ],
                    "type": "doc"
                  }
                },
                "id": "4339b9553ff04515883bcedf70077a61",
                "layout": {
                  "spec": {
                    "height": null,
                    "type": "calculated-height",
                    "width": "100%"
                  },
                  "verticalAlign": "center"
                },
                "position": "a3"
              },
              "block_uuid": "4339b9553ff04515883bcedf70077a61",
              "content_uuid": null,
              "created_at": "Wed, 26 Nov 2025 20:18:44 GMT",
              "id": 101,
              "node_id": 4,
              "position": "a3",
              "updated_at": "Wed, 03 Dec 2025 20:30:06 GMT"
            },
            {
              "block": {
                "content": {
                  "contentUUID": null,
                  "horizontalAlignment": "left",
                  "type": "text",
                  "value": "<p class=\"nodeview-paragraph\">Journey empowers B2B sales teams to create personalized buyer experiences that drive engagement and close deals faster. Instead of sending multiple emails with scattered attachments, consolidate everything into one beautiful, interactive microsite.</p><p class=\"nodeview-paragraph\" style=\"text-align: start\">Real-time collaboration features allow your team to work together on Journeys, with comments, version updates, and shared timelines. Access reusable templates and brand kits to ensure consistency across all your sales communications while maintaining your unique brand identity.</p><p class=\"nodeview-paragraph\" style=\"text-align: start\">Journey's powerful analytics give you insights into how buyers interact with your content. See which sections they spend the most time on, track scroll depth, and measure click-through rates to inform your follow-up strategy and improve conversion rates.</p><p class=\"nodeview-paragraph\" style=\"text-align: start\"><strong>Benefits</strong></p><ul><li><div></div><div><p class=\"nodeview-paragraph\">Increase engagement: Buyers spend more time with consolidated, interactive content</p></div></li><li><div></div><div><p class=\"nodeview-paragraph\">Close deals faster: Personalized experiences build trust and accelerate decision-making</p></div></li><li><div></div><div><p class=\"nodeview-paragraph\">Save time: No more managing multiple attachments and follow-up emails</p></div></li><li><div></div><div><p class=\"nodeview-paragraph\">Track performance: Detailed analytics help you understand buyer behavior</p></div></li><li><div></div><div><p class=\"nodeview-paragraph\">Scale personalization: Create customized experiences for each prospect at scale</p></div></li><li><div></div><div><p class=\"nodeview-paragraph\">Professional presentation: Branded microsites make a lasting impression</p></div></li><li><div></div><div><p class=\"nodeview-paragraph\">Mobile-first: Buyers can access your content from any device, anywhere</p></div></li><li><div></div><div><p class=\"nodeview-paragraph\">Easy sharing: One link contains everything your buyer needs to know</p></div></li></ul><p class=\"nodeview-paragraph\" style=\"text-align: start\">Start your 14-day free trial today and see how Journey can transform your B2B sales process.</p>",
                  "valueJSON": {
                    "content": [
                      {
                        "attrs": {
                          "textAlign": "left"
                        },
                        "content": [
                          {
                            "text": "Journey empowers B2B sales teams to create personalized buyer experiences that drive engagement and close deals faster. Instead of sending multiple emails with scattered attachments, consolidate everything into one beautiful, interactive microsite.",
                            "type": "text"
                          }
                        ],
                        "type": "paragraph"
                      },
                      {
                        "attrs": {
                          "textAlign": "start"
                        },
                        "content": [
                          {
                            "text": "Real-time collaboration features allow your team to work together on Journeys, with comments, version updates, and shared timelines. Access reusable templates and brand kits to ensure consistency across all your sales communications while maintaining your unique brand identity.",
                            "type": "text"
                          }
                        ],
                        "type": "paragraph"
                      },
                      {
                        "attrs": {
                          "textAlign": "start"
                        },
                        "content": [
                          {
                            "text": "Journey's powerful analytics give you insights into how buyers interact with your content. See which sections they spend the most time on, track scroll depth, and measure click-through rates to inform your follow-up strategy and improve conversion rates.",
                            "type": "text"
                          }
                        ],
                        "type": "paragraph"
                      },
                      {
                        "attrs": {
                          "textAlign": "start"
                        },
                        "content": [
                          {
                            "marks": [
                              {
                                "type": "bold"
                              }
                            ],
                            "text": "Benefits",
                            "type": "text"
                          }
                        ],
                        "type": "paragraph"
                      },
                      {
                        "content": [
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Increase engagement: Buyers spend more time with consolidated, interactive content",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          },
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Close deals faster: Personalized experiences build trust and accelerate decision-making",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          },
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Save time: No more managing multiple attachments and follow-up emails",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          },
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Track performance: Detailed analytics help you understand buyer behavior",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          },
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Scale personalization: Create customized experiences for each prospect at scale",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          },
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Professional presentation: Branded microsites make a lasting impression",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          },
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Mobile-first: Buyers can access your content from any device, anywhere",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          },
                          {
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Easy sharing: One link contains everything your buyer needs to know",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "listItem"
                          }
                        ],
                        "type": "bulletList"
                      },
                      {
                        "attrs": {
                          "textAlign": "start"
                        },
                        "content": [
                          {
                            "text": "Start your 14-day free trial today and see how Journey can transform your B2B sales process.",
                            "type": "text"
                          }
                        ],
                        "type": "paragraph"
                      }
                    ],
                    "type": "doc"
                  }
                },
                "id": "10336b0b7fbd436494ab6200f3e075b5",
                "layout": {
                  "spec": {
                    "height": null,
                    "type": "calculated-height",
                    "width": "100%"
                  },
                  "verticalAlign": "center"
                },
                "position": "a1"
              },
              "block_uuid": "10336b0b7fbd436494ab6200f3e075b5",
              "content_uuid": null,
              "created_at": "Wed, 12 Nov 2025 05:38:54 GMT",
              "id": 97,
              "node_id": 4,
              "position": "a1",
              "updated_at": "Wed, 03 Dec 2025 18:03:35 GMT"
            },
            {
              "block": {
                "content": {
                  "contentUUID": null,
                  "horizontalAlignment": "left",
                  "type": "text",
                  "value": "<h2 class=\"nodeview-heading \"><strong>Why Choose Journey</strong></h2>",
                  "valueJSON": {
                    "content": [
                      {
                        "attrs": {
                          "huge": false,
                          "level": 2,
                          "textAlign": "left"
                        },
                        "content": [
                          {
                            "marks": [
                              {
                                "type": "bold"
                              }
                            ],
                            "text": "Why Choose Journey",
                            "type": "text"
                          }
                        ],
                        "type": "heading"
                      }
                    ],
                    "type": "doc"
                  }
                },
                "id": "ad9fb47e16944913ab2b282868c38ed7",
                "layout": {
                  "spec": {
                    "height": null,
                    "type": "calculated-height",
                    "width": "100%"
                  },
                  "verticalAlign": "center"
                },
                "position": "a0"
              },
              "block_uuid": "ad9fb47e16944913ab2b282868c38ed7",
              "content_uuid": null,
              "created_at": "Wed, 12 Nov 2025 05:38:29 GMT",
              "id": 89,
              "node_id": 4,
              "position": "a0",
              "updated_at": "Wed, 03 Dec 2025 18:28:07 GMT"
            },
            {
              "block": {
                "content": {
                  "horizontalAlignment": "left",
                  "type": "text",
                  "value": "<h2 class=\"nodeview-heading \"><strong>How to find success with Journey&nbsp;</strong></h2><ul class=\"relative nodeview-task-list\" data-type=\"taskList\"><li data-checked=\"false\" data-type=\"taskItem\"><label><input type=\"checkbox\"><span></span></label><div><p class=\"nodeview-paragraph\">Pick 1 use case and create your first Journey&nbsp;</p></div></li><li data-checked=\"false\" data-type=\"taskItem\"><label><input type=\"checkbox\"><span></span></label><div><p class=\"nodeview-paragraph\">Share it internally or with the Journey team for feedback&nbsp;</p></div></li><li data-checked=\"false\" data-type=\"taskItem\"><label><input type=\"checkbox\"><span></span></label><div><p class=\"nodeview-paragraph\">Share it externally with your recipients&nbsp;</p></div></li><li data-checked=\"false\" data-type=\"taskItem\"><label><input type=\"checkbox\"><span></span></label><div><p class=\"nodeview-paragraph\">Review insights to understand their engagement&nbsp;</p></div></li><li data-checked=\"false\" data-type=\"taskItem\"><label><input type=\"checkbox\"><span></span></label><div><p class=\"nodeview-paragraph\">Optimize your content based on the outcomes you're looking to drive&nbsp;</p></div></li></ul>",
                  "valueJSON": {
                    "content": [
                      {
                        "attrs": {
                          "huge": false,
                          "level": 2,
                          "textAlign": "left"
                        },
                        "content": [
                          {
                            "marks": [
                              {
                                "type": "bold"
                              }
                            ],
                            "text": "How to find success with Journey ",
                            "type": "text"
                          }
                        ],
                        "type": "heading"
                      },
                      {
                        "content": [
                          {
                            "attrs": {
                              "checked": false
                            },
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Pick 1 use case and create your first Journey ",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "taskItem"
                          },
                          {
                            "attrs": {
                              "checked": false
                            },
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Share it internally or with the Journey team for feedback ",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "taskItem"
                          },
                          {
                            "attrs": {
                              "checked": false
                            },
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Share it externally with your recipients ",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "taskItem"
                          },
                          {
                            "attrs": {
                              "checked": false
                            },
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Review insights to understand their engagement ",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "taskItem"
                          },
                          {
                            "attrs": {
                              "checked": false
                            },
                            "content": [
                              {
                                "attrs": {
                                  "textAlign": "left"
                                },
                                "content": [
                                  {
                                    "text": "Optimize your content based on the outcomes you're looking to drive ",
                                    "type": "text"
                                  }
                                ],
                                "type": "paragraph"
                              }
                            ],
                            "type": "taskItem"
                          }
                        ],
                        "type": "taskList"
                      }
                    ],
                    "type": "doc"
                  }
                },
                "id": "92386a530ffc47d4b78410bb872b8cd0",
                "layout": {
                  "spec": {
                    "height": null,
                    "type": "calculated-height",
                    "width": "100%"
                  },
                  "verticalAlign": "center"
                },
                "position": "a1V"
              },
              "block_uuid": "92386a530ffc47d4b78410bb872b8cd0",
              "content_uuid": null,
              "created_at": "Wed, 03 Dec 2025 20:19:25 GMT",
              "id": 211,
              "node_id": 4,
              "position": "a1V",
              "updated_at": "Wed, 03 Dec 2025 20:20:08 GMT"
            },
            {
              "block": {
                "content": {
                  "displayOption": "inline",
                  "link": {
                    "destination": {
                      "host": "loom.com",
                      "logo_url": null
                    },
                    "iframe_safe": true,
                    "third_party_iframe_safe": true,
                    "thumbnail": {
                      "height": null,
                      "url": null,
                      "width": null
                    },
                    "transformed_url": "https://www.loom.com/embed/ee05dfac4b574e788d16a0bb80c6cfe3",
                    "url": "https://www.loom.com/embed/ee05dfac4b574e788d16a0bb80c6cfe3"
                  },
                  "type": "link"
                },
                "id": "93973207686d4e4f99318c0188a04d47",
                "layout": {
                  "spec": {
                    "height": null,
                    "type": "calculated-height",
                    "width": "100%"
                  },
                  "verticalAlign": "top"
                },
                "position": "a1l"
              },
              "block_uuid": "93973207686d4e4f99318c0188a04d47",
              "content_uuid": null,
              "created_at": "Wed, 03 Dec 2025 20:30:10 GMT",
              "id": 238,
              "node_id": 4,
              "position": "a1l",
              "updated_at": "Wed, 03 Dec 2025 20:30:26 GMT"
            }
          ],
          "parent_node_id": null,
          "slug": null,
          "step_node_id": 4,
          "synced_name": null,
          "text": null,
          "thumbnail": null,
          "type": {
            "data": null,
            "friendly_name": "Neue",
            "id": 12,
            "name": "neue"
          },
          "updated_at": "2025-11-12T05:38:27.784306",
          "user_id": 1,
          "user_prospect": null,
          "uuid": "1297d72083614ebdb5d8aa003140896f",
          "video_asset": null
        }
      ],
      "position": 1,
      "type": "n"
    }
  ],
  "tags": [],
  "theme": {
    "brand_theme_color": "#FFFFFF",
    "custom_theme_values": null,
    "dynamic_font_preferences": {
      "headings": {
        "font_family": null,
        "font_variant": null
      },
      "paragraphs": {
        "font_family": null,
        "font_variant": null
      }
    },
    "font_preferences": {
      "headings": {
        "font_family": null,
        "font_weight": null
      },
      "paragraphs": {
        "font_family": null,
        "font_weight": null
      },
      "size_multiplier": null
    },
    "theme_type": "light"
  },
  "thumbnail_url": "https://assets-raw.jny.io/thumbnails/ce08c03c742b4bcfaa403f14fecdf1d8.png",
  "updated_at": "2025-12-03T20:30:26.125366",
  "uuid": "cfac29fd696242ec9f316e7e2d9b8b2a",
  "workspace": {
    "id": 1,
    "name": "Team"
  }
} as any;

// Static journey data - populated with captured API responses
// Format: { 'journey-uuid': { ... journey data ... }, ... }
// This will be used when USE_API_CALLS is false
const STATIC_JOURNEY_DATA: Record<string, any> = {
  'cfac29fd696242ec9f316e7e2d9b8b2a': CAPTURED_JOURNEY_DATA,
};

// Static interaction response - populated with captured API response
// This will be used when USE_API_CALLS is false
const STATIC_INTERACTION_RESPONSE: any = {
  success: true,
  message: 'Changes are not saved in embedded mode',
};

// Static user data - populated with captured API response
// UUID: 59546d0594d34e15bf4e00b6058ca4f6
// This data was captured from the actual API response and is used when USE_API_CALLS is false
const STATIC_USER_DATA: any = {
  api_key: '62df5e27bbc484d968b2c414bab4a37a8adf5a88f8b1715a38c970b12f0164d1',
  avatar_url: null,
  created_at: '2025-11-04T09:20:26.280790',
  email: 'goutham@famey.io',
  has_password: false,
  id: 1,
  is_admin: false,
  is_product_tour_complete: true,
  last_organization: {
    branding: {
      custom_meta_description: null,
      custom_title: null,
      force_generated_colors: false,
      heading_typeface: 'serif',
      hide_journey_upsell: false,
      icon_url: null,
      is_applied: true,
      journey_thumbnail_url: 'https://assets-raw.jny.io/thumbnails/ce08c03c742b4bcfaa403f14fecdf1d8.png',
      logo_url: null,
      primary_color: null,
      secondary_color: null,
      thumbnail_style: 'image',
    },
    created_at: '2025-11-04T09:20:26.287456',
    custom_domain: null,
    disabled: false,
    feature_flags: {},
    has_access_to_neue_only: false,
    id: 1,
    is_locked: false,
    is_onboarded: true,
    is_subscribed: false,
    journey_count: 2,
    name: 'Famey',
    num_of_billable_users: 1,
    num_of_users: 1,
    quota: null,
    settings: {
      journey_email_required: false,
      journey_email_verification_required: false,
    },
    slug: 'famey',
    subscription_status: null,
    trial_end_at: '2025-11-18T09:20:26.287479',
    trial_expired: false,
    trial_start_at: '2025-11-04T09:20:26.287479',
    url: 'https://famey.io',
    users: [
      {
        email: 'goutham@famey.io',
        id: 1,
        name: 'goutham@famey.io',
      },
    ],
    uuid: 'e92230204fb047f09641406382a030b3',
    workspaces: [
      {
        id: 1,
        members: [
          {
            email: 'goutham@famey.io',
            id: 1,
            name: 'goutham@famey.io',
          },
        ],
        name: 'Team',
        num_of_journeys: 2,
        workspace_type: 'public',
      },
      {
        id: 2,
        members: [
          {
            email: 'goutham@famey.io',
            id: 1,
            name: 'goutham@famey.io',
          },
        ],
        name: 'Private',
        num_of_journeys: 0,
        workspace_type: 'standard',
      },
    ],
  },
  last_seen_product_update: null,
  name: 'goutham@famey.io',
  nolt_token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MSwiZW1haWwiOiJnb3V0aGFtQGZhbWV5LmlvIiwibmFtZSI6ImdvdXRoYW1AZmFtZXkuaW8ifQ.oUkY41wJY7aCxYSzKjKUXiu-cVLm1DI71uIKJFKYAEY',
  onboarding_step: '1',
  organization: {
    branding: {
      custom_meta_description: null,
      custom_title: null,
      force_generated_colors: false,
      heading_typeface: 'serif',
      hide_journey_upsell: false,
      icon_url: null,
      is_applied: true,
      journey_thumbnail_url: 'https://assets-raw.jny.io/thumbnails/ce08c03c742b4bcfaa403f14fecdf1d8.png',
      logo_url: null,
      primary_color: null,
      secondary_color: null,
      thumbnail_style: 'image',
    },
    created_at: '2025-11-04T09:20:26.287456',
    custom_domain: null,
    disabled: false,
    feature_flags: {},
    has_access_to_neue_only: false,
    id: 1,
    is_locked: false,
    is_onboarded: true,
    is_subscribed: false,
    journey_count: 2,
    name: 'Famey',
    num_of_billable_users: 1,
    num_of_users: 1,
    quota: null,
    settings: {
      journey_email_required: false,
      journey_email_verification_required: false,
    },
    slug: 'famey',
    subscription_status: null,
    trial_end_at: '2025-11-18T09:20:26.287479',
    trial_expired: false,
    trial_start_at: '2025-11-04T09:20:26.287479',
    url: 'https://famey.io',
    users: [
      {
        email: 'goutham@famey.io',
        id: 1,
        name: 'goutham@famey.io',
      },
    ],
    uuid: 'e92230204fb047f09641406382a030b3',
    workspaces: [
      {
        id: 1,
        members: [
          {
            email: 'goutham@famey.io',
            id: 1,
            name: 'goutham@famey.io',
          },
        ],
        name: 'Team',
        num_of_journeys: 2,
        workspace_type: 'public',
      },
      {
        id: 2,
        members: [
          {
            email: 'goutham@famey.io',
            id: 1,
            name: 'goutham@famey.io',
          },
        ],
        name: 'Private',
        num_of_journeys: 0,
        workspace_type: 'standard',
      },
    ],
  },
  persona: {
    code: 'customer_success',
    name: 'Customer Success',
  },
  purpose: '{"Marketing presentations"}',
  story_other: null,
  story_type: 'education',
  updated_at: '2025-11-04T09:20:26.280790',
  uuid: '59546d0594d34e15bf4e00b6058ca4f6',
};

// Static organization data - populated with captured API response
// UUID: e92230204fb047f09641406382a030b3
// This data was captured from the actual API response and is used when USE_API_CALLS is false
const STATIC_ORGANIZATION_DATA: any = {
  branding: {
    custom_meta_description: null,
    custom_title: null,
    force_generated_colors: false,
    heading_typeface: 'serif',
    hide_journey_upsell: false,
    icon_url: null,
    is_applied: true,
    journey_thumbnail_url: 'https://assets-raw.jny.io/thumbnails/ce08c03c742b4bcfaa403f14fecdf1d8.png',
    logo_url: null,
    primary_color: null,
    secondary_color: null,
    thumbnail_style: 'image',
  },
  created_at: '2025-11-04T09:20:26.287456',
  custom_domain: null,
  disabled: false,
  feature_flags: {},
  has_access_to_neue_only: false,
  id: 1,
  is_locked: false,
  is_onboarded: true,
  is_subscribed: false,
  journey_count: 2,
  name: 'Famey',
  num_of_billable_users: 1,
  num_of_users: 1,
  quota: null,
  settings: {
    journey_email_required: false,
    journey_email_verification_required: false,
  },
  slug: 'famey',
  subscription_status: null,
  trial_end_at: '2025-11-18T09:20:26.287479',
  trial_expired: false,
  trial_start_at: '2025-11-04T09:20:26.287479',
  url: 'https://famey.io',
  users: [
    {
      email: 'goutham@famey.io',
      id: 1,
      name: 'goutham@famey.io',
    },
  ],
  uuid: 'e92230204fb047f09641406382a030b3',
  workspaces: [
    {
      id: 1,
      members: [
        {
          email: 'goutham@famey.io',
          id: 1,
          name: 'goutham@famey.io',
        },
      ],
      name: 'Team',
      num_of_journeys: 2,
      workspace_type: 'public',
    },
    {
      id: 2,
      members: [
        {
          email: 'goutham@famey.io',
          id: 1,
          name: 'goutham@famey.io',
        },
      ],
      name: 'Private',
      num_of_journeys: 0,
      workspace_type: 'standard',
    },
  ],
};

// Helper function to get static journey data
const getStaticJourneyData = (uuid: string): any | null => {
  return STATIC_JOURNEY_DATA[uuid] || null;
};

// Helper function to get static interaction response
const getStaticInteractionResponse = (): any => {
  return STATIC_INTERACTION_RESPONSE;
};

// Helper function to get static user data
const getStaticUserData = (): any | null => {
  return Object.keys(STATIC_USER_DATA).length > 0 ? STATIC_USER_DATA : null;
};

// Helper function to get static organization data
const getStaticOrganizationData = (): any | null => {
  return Object.keys(STATIC_ORGANIZATION_DATA).length > 0 ? STATIC_ORGANIZATION_DATA : null;
};

// Helper function to capture and store API response for easy copying
const captureApiResponse = (endpoint: string, uuid: string, response: any) => {
  const captureData = {
    endpoint,
    uuid,
    timestamp: new Date().toISOString(),
    response,
  };
  
  // Save to localStorage for persistence across page reloads
  try {
    const existingCaptures = JSON.parse(localStorage.getItem('api_response_captures') || '[]');
    existingCaptures.push(captureData);
    // Keep only last 10 captures
    const recentCaptures = existingCaptures.slice(-10);
    localStorage.setItem('api_response_captures', JSON.stringify(recentCaptures));
  } catch (e) {
    // Silently fail if localStorage is not available
  }
};
// ============================================================================

// Demo journey data for landing-demo mode
const createDemoJourney = (uuid: string) => ({
  uuid,
  name: 'Demo Journey',
  slug: 'demo-journey',
  is_neue: true,
  created_at: new Date().toISOString(),
  can_edit: true,
  updated_at: new Date().toISOString(),
  is_folders_enabled: false,
  is_gallery_mode_enabled: false,
  aliases: [],
  alias: {
    uuid: 'demo-alias-uuid',
    slug: 'demo-journey',
  },
  branding: null,
  theme: {
    dynamic_font_preferences: {
      headings: { font_family: 'CircularXX', font_variant: 'regular' },
      paragraphs: { font_family: 'CircularXX', font_variant: 'regular' },
    },
  },
  creator: {
    uuid: 'demo-creator-uuid',
    email: 'demo@journey.io',
    name: 'Demo User',
  },
  organization: {
    name: 'Demo Organization',
    slug: 'demo-org',
    url: 'https://demo.journey.io',
    custom_domain: null,
  },
  annotation: null,
  feature_flags: {},
  engagements: {
    views: 0,
    completions: 0,
  },
  workspace: {
    id: 1,
    name: 'Demo Workspace',
    slug: 'demo-workspace',
  },
  tags: [],
  thumbnail_url: null,
  status: 'draft',
  cta: null,
  prospect_personalization: null,
  steps: [
    {
      id: 1,
      position: 0,
      nodes: [
        {
          uuid: 'demo-node-uuid-1',
          id: 1,
          name: 'Welcome',
          name_set_manually: false,
          hidden: false,
          folder_key: null,
          folder_name: null,
          annotations: [],
          node_blocks: [
            {
              position: 0,
              block: {
                id: 'demo-block-1',
                content: {
                  type: 'doc',
                  content: [
                    {
                      type: 'heading',
                      attrs: { level: 1 },
                      content: [{ type: 'text', text: 'Welcome to Journey' }],
                    },
                  ],
                },
                layout: null,
              },
            },
            {
              position: 1,
              block: {
                id: 'demo-block-2',
                content: {
                  type: 'doc',
                  content: [
                    {
                      type: 'paragraph',
                      content: [
                        {
                          type: 'text',
                          text: 'This is a demo journey. Try editing the content!',
                        },
                      ],
                    },
                  ],
                },
                layout: null,
              },
            },
          ],
        },
      ],
    },
  ],
  num_of_steps: 1,
  is_template: false,
  social_sharing: true,
});

/**
 * Mobile journey editor component that works without authentication.
 * Designed to be embedded in landing pages and other external contexts.
 * 
 * Features:
 * - Works without user authentication (handles null/undefined user/org gracefully)
 * - Supports demo mode via ?mode=landing-demo URL parameter
 * - Gracefully handles API errors and missing data
 * - Skips interaction tracking when not authenticated
 * - ALL changes are blocked from being saved to backend (read-only mode for embedding)
 *   - Blocks PUT, POST, PATCH, DELETE operations for journeys, nodes, sections, blocks, steps, annotations
 *   - Only GET requests are allowed (read-only)
 */
export const PageJourneysContentCreationEditMobile: VFC<PageJourneysContentCreationEditMobileProps> = ({ uuid, sectionPath }) => {
  // Check for landing-demo mode
  const isLandingDemo = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') === 'landing-demo';
  }, []);

  const preventLogs = useFeatureFlag('prevent_logs');

  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const setCurrentOrganization = useCurrentOrganization((state) => state.setCurrentOrganization);

  const initDynamicFonts = useJourneyDynamicFontStore((state) => state.init);
  const currentDynamicFont = useJourneyDynamicFontStore((state) => state.current);
  const isDynamicFontsReady = useJourneyDynamicFontStore((state) => state.isReady);
  const setIsCustomFontStrictlyUsed = useJourneyDynamicFontStore((state) => state.setIsCustomFontStrictlyUsed);

  const journey = useEditorContext((state) => state.journey);
  const setJourney = useEditorContext((state) => state.setJourney);
  const setAnnotation = useEditorContext((state) => state.setAnnotation);
  const resetEditor = useEditorContext((state) => state.resetEditor);
  const setAliasAnnotationNodeId = useEditorContext((state) => state.setAliasAnnotationNodeId);

  const resetNeueEditor = useEditorStore((state) => state.resetEditor);
  const initEditor = useEditorStore((state) => state.initEditor);

  const resetTalkToJourney = useTalkToJourneyStore((state) => state.reset);

  const isInitialized = useClipboardStore((state) => state.isInitialized, shallow);
  const initClipboard = useClipboardStore((state) => state.initClipboard);

  const currentUser = useCurrentUser((state) => state.currentUser);
  const setCurrentUser = useCurrentUser((state) => state.setCurrentUser);
  const setOrgData = usePlayerStore((state) => state.setOrgData);

  // Enable embedded mode to block all saves to backend
  useEffect(() => {
    setEmbeddedMode(true);
    return () => {
      setEmbeddedMode(false);
    };
  }, []);

  // Initialize static user and organization data when using static mode (for incognito/unauthenticated access)
  useEffect(() => {
    if (!isLandingDemo && !USE_API_CALLS) {
      // Use static user data if available and not already set
      const staticUserData = getStaticUserData();
      if (staticUserData) {
        // Only set if not already set or if UUIDs don't match
        if (!currentUser || currentUser.uuid !== staticUserData.uuid) {
          setCurrentUser(staticUserData);
        }
      }
      
      // Use static organization data if available and not already set
      const staticOrgData = getStaticOrganizationData();
      if (staticOrgData) {
        // Only set if not already set or if UUIDs don't match
        if (!currentOrganization || currentOrganization.uuid !== staticOrgData.uuid) {
          setCurrentOrganization(staticOrgData);
        }
      }
    }
  }, []); // Run only on mount

  // Capture currentUser and currentOrganization data for static data replacement (when using API calls)
  useEffect(() => {
    if (!isLandingDemo && USE_API_CALLS) {
      // CAPTURE USER DATA FOR STATIC DATA REPLACEMENT
      if (currentUser) {
        const userCaptureKey = `user_captured_${currentUser.uuid}`;
        if (!sessionStorage.getItem(userCaptureKey)) {
          captureApiResponse('currentUser', currentUser.uuid || 'unknown', currentUser);
          sessionStorage.setItem(userCaptureKey, 'true');
        }
      }
      
      // CAPTURE ORGANIZATION DATA FOR STATIC DATA REPLACEMENT
      if (currentOrganization) {
        const orgCaptureKey = `org_captured_${currentOrganization.uuid}`;
        if (!sessionStorage.getItem(orgCaptureKey)) {
          captureApiResponse('currentOrganization', currentOrganization.uuid || 'unknown', currentOrganization);
          sessionStorage.setItem(orgCaptureKey, 'true');
        }
      }
    }
  }, [currentUser, currentOrganization, isLandingDemo]);

  useEffect(() => {
    if (preventLogs) {
      console.log = functionNoop;
    }
    return () => {
      console.log = originalConsoleLog;
    };
  }, [preventLogs]);

  useEffect(() => {
    const onDocumentFocus = async () => {
      await initClipboard();
      window.removeEventListener('focus', onDocumentFocus);
    };

    if (isInitialized) {
      return;
    }
    window.addEventListener('focus', onDocumentFocus);
    return () => {
      window.removeEventListener('focus', onDocumentFocus);
    };
  }, [isInitialized]);

  // Always block all save operations to backend (demo/embedding mode)
  // This prevents any changes from being persisted when embedded in landing pages
  useEffect(() => {
    // Intercept axios requests to block ALL save/update/delete operations
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const url = config.url || '';
        const method = config.method?.toUpperCase() || '';

        // Block ALL save/update/delete operations for journeys
        if (
          url.includes('/api/journeys/') &&
          (method === 'PUT' || method === 'POST' || method === 'PATCH' || method === 'DELETE')
        ) {
          // Block all journey modifications (dispatch, interaction, update, delete, etc.)
          // Allow only GET requests (read-only)
          return Promise.reject({
            isEmbeddedBlocked: true,
            data: { success: true, message: 'Changes are not saved in embedded mode' },
            config,
          });
        }

        // Block ALL save operations for journey content (nodes, sections, blocks)
        if (
          (method === 'PUT' || method === 'POST' || method === 'PATCH' || method === 'DELETE') &&
          (url.includes('/api/journey-nodes/') ||
            url.includes('/api/journey-sections/') ||
            url.includes('/api/journey-blocks/') ||
            url.includes('/api/journey-steps/') ||
            url.includes('/api/annotations/'))
        ) {
          return Promise.reject({
            isEmbeddedBlocked: true,
            data: { success: true, message: 'Changes are not saved in embedded mode' },
            config,
          });
        }

        // Block the neue dispatch endpoint (primary save endpoint)
        if (url.includes('/api/neue/dispatch') && method === 'POST') {
          return Promise.reject({
            isEmbeddedBlocked: true,
            data: { success: true, message: 'Changes are not saved in embedded mode' },
            config,
          });
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Intercept axios responses to handle blocked requests
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.isEmbeddedBlocked) {
          // Return mock success response for blocked requests
          // This prevents errors from breaking the UI while still blocking saves
          return Promise.resolve({
            data: error.data || { success: true, message: 'Changes are not saved in embedded mode' },
            status: 200,
            statusText: 'OK',
            headers: {},
            config: error.config,
          });
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Setup demo mode: auto-login and block destructive actions
  useEffect(() => {
    if (!isLandingDemo) return;

    // Create demo organization
    const demoOrganization = {
      uuid: 'demo-org-uuid',
      id: 1,
      name: 'Demo Organization',
      slug: 'demo-org',
      url: 'https://demo.journey.io',
      custom_domain: null,
      branding: {
        secondary_color: '#000000',
        primary_color: '#000000',
        heading_typeface: 'sans_serif' as const,
        custom_colors: {},
        hide_journey_upsell: false,
        is_applied: false,
        icon_url: null,
        force_generated_colors: false,
      },
      feature_flags: {},
      users: [],
      subscription_status: null,
      journey_count: 0,
      isRoot: false,
      disabled: false,
      is_subscribed: false,
      workspaces: [],
      num_of_billable_users: 0,
      num_of_users: 1,
      settings: {
        journey_email_required: false,
        journey_email_verification_required: false,
      },
      has_access_to_neue_only: false,
      created_at: new Date().toISOString(),
    };

    // Create demo user
    const demoUser = {
      id: 1,
      uuid: 'demo-user-uuid',
      email: 'demo@journey.io',
      name: 'Demo User',
      created_at: new Date().toISOString(),
      role: 'admin' as const,
      is_admin: true,
      api_key: 'demo-api-key',
      has_password: false,
      is_product_tour_complete: false,
      avatar_url: null,
      persona: {
        code: 'demo',
        name: 'Demo',
      },
      organization: demoOrganization,
      last_organization: demoOrganization,
    };

    // Set demo user and organization in stores
    const { setCurrentUser } = useCurrentUser.getState();
    const { setCurrentOrganization, setFreeOrg } = useCurrentOrganization.getState();
    setCurrentUser(demoUser as any);
    setCurrentOrganization(demoOrganization);
    setFreeOrg(demoOrganization);

    // Intercept axios requests to block destructive actions
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const url = config.url || '';
        const method = config.method?.toUpperCase() || '';

        // Block save/update/delete operations for journeys
        if (
          url.includes('/api/journeys/') &&
          (method === 'PUT' || method === 'POST' || method === 'PATCH' || method === 'DELETE')
        ) {
          // Block dispatch, interaction, update, and delete endpoints
          if (
            url.includes('/dispatch') ||
            url.includes('/interaction') ||
            url.includes('/update') ||
            url.match(/\/api\/journeys\/[^/]+\/?$/) || // Direct journey update
            method === 'DELETE'
          ) {
            // Return a mock successful response
            return Promise.reject({
              isDemoBlocked: true,
              data: { success: true, demo: true },
              config,
            });
          }
        }

        // Block other destructive actions
        if (
          (method === 'PUT' || method === 'POST' || method === 'PATCH' || method === 'DELETE') &&
          (url.includes('/api/journey-nodes/') ||
            url.includes('/api/journey-sections/') ||
            url.includes('/api/journey-blocks/'))
        ) {
          return Promise.reject({
            isDemoBlocked: true,
            data: { success: true, demo: true },
            config,
          });
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Intercept axios responses to handle blocked requests
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.isDemoBlocked) {
          // Return mock success response for blocked requests
          return Promise.resolve({
            data: error.data || { success: true, demo: true },
            status: 200,
            statusText: 'OK',
            headers: {},
            config: error.config,
          });
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [isLandingDemo]);

  useEffect(() => {
    const fetchJourney = async (uuid: string) => {
      let jny;
      
      try {
        if (isLandingDemo) {
          // Use demo journey data
          jny = createDemoJourney(uuid);
        } else if (!USE_API_CALLS) {
          // Use static data instead of API call
          const staticData = getStaticJourneyData(uuid);
          if (staticData) {
            jny = staticData;
          } else {
            // Fallback to API if static data not available
            jny = await apiGetJourney(uuid);
          }
        } else {
          // Fetch real journey - works without authentication for public journeys
          jny = await apiGetJourney(uuid);
          
          // CAPTURE API RESPONSE FOR STATIC DATA REPLACEMENT
          captureApiResponse('apiGetJourney', uuid, jny);
          
          // DEBUG: Log current journey data for comparison with CAPTURED_JOURNEY_DATA
          console.log('========================================');
          console.log('DEBUG: Current Journey Data (USE_API_CALLS = true)');
          console.log('Journey UUID:', uuid);
          console.log('========================================');
          console.log('Current Journey Data:', JSON.stringify(jny, null, 2));
          console.log('========================================');
          console.log('To update CAPTURED_JOURNEY_DATA, copy the JSON above');
          console.log('========================================');
          
          // Also log a comparison if CAPTURED_JOURNEY_DATA exists for this UUID
          const capturedData = getStaticJourneyData(uuid);
          if (capturedData) {
            console.log('========================================');
            console.log('COMPARISON: Current vs Captured Journey Data');
            console.log('========================================');
            console.log('Current Journey UUID:', jny?.uuid);
            console.log('Captured Journey UUID:', capturedData?.uuid);
            console.log('Current Journey Name:', jny?.name);
            console.log('Captured Journey Name:', capturedData?.name);
            console.log('Current Steps Count:', jny?.steps?.length || 0);
            console.log('Captured Steps Count:', capturedData?.steps?.length || 0);
            console.log('Current Updated At:', jny?.updated_at);
            console.log('Captured Updated At:', capturedData?.updated_at);
            console.log('========================================');
          }
        }
      } catch (error) {
        console.error('Failed to fetch journey:', error);
        // Return null to prevent rendering if journey fetch fails
        return null;
      }

      if (!jny) {
        return null;
      }

      setOrgData(jny.organization.slug, jny.creator.uuid === currentUser?.uuid);
      setJourney(jny);

      const brandColor = currentOrganization?.branding?.secondary_color || null;
      const themeSettings = getJourneyThemeSettings(jny.theme, brandColor);
      const sections = journeyToSections(jny);
      const initialSectionShortId = sectionPath ? getIdFromFriendlySectionPath(sectionPath) : null;
      const initialSection = getFirstSection(sections);
      if (initialSection) {
        setAliasAnnotationNodeId(initialSection.aliasAnnotationNodeId || null);
      }
      let initialSectionId = null;
      if (initialSectionShortId) {
        // Validate that the section exists
        const section = sections.find((section) => section.id.startsWith(initialSectionShortId));
        initialSectionId = section ? section.id : null;
      }
      await loadAllFontFamilies();
      
      // Provide default feature flags if organization is not available (no-auth mode)
      const featureFlags = currentOrganization?.feature_flags || {};
      
      await initEditor(
        jny.uuid,
        jny.name,
        journeyToSections(jny),
        themeSettings,
        featureFlags,
        initialSectionId
      );
      const journeyAnnotation = getAliasAnnotation(jny);
      if (journeyAnnotation && journeyAnnotation.id) {
        setAnnotation(converAnnotationToForm(journeyAnnotation));
      }
      // await initClipboard();
      // Skip interaction tracking in demo mode or when not authenticated
      if (!isLandingDemo && currentUser) {
        try {
          let interactionResponse;
          
          if (!USE_API_CALLS) {
            // Use static data instead of API call
            interactionResponse = getStaticInteractionResponse();
          } else {
            // Make actual API call and capture response
            interactionResponse = await apiPostJourneyInteractionOpen(uuid);
            
            // CAPTURE API RESPONSE FOR STATIC DATA REPLACEMENT
            captureApiResponse('apiPostJourneyInteractionOpen', uuid, interactionResponse);
          }
        } catch (error) {
          // Silently fail interaction tracking if not authenticated
          console.warn('Failed to track journey interaction:', error);
        }
      }
      const dynamicFonts = {
        headings: {
          font_family: jny.theme.dynamic_font_preferences?.headings?.font_family || 'CircularXX',
          font_variant: jny.theme.dynamic_font_preferences?.headings?.font_variant || 'regular',
        },
        paragraphs: {
          font_family: jny.theme.dynamic_font_preferences?.paragraphs?.font_family || 'CircularXX',
          font_variant: jny.theme.dynamic_font_preferences?.paragraphs?.font_variant || 'regular',
        },
      };

      return dynamicFonts;
    };

    if (uuid) {
      fetchJourney(uuid)
        .then((dynamicFonts) => {
          if (dynamicFonts) {
            initDynamicFonts(dynamicFonts);
          }
        })
        .then(() => {
          if (!currentOrganization) {
            return;
          }
          const loadCustomFont = isCustomFontStrictlyUsedForOrg(currentOrganization.slug);
          setIsCustomFontStrictlyUsed(loadCustomFont);
          applyStrictCustomFont({ variant: 'regular' });
        })
        .catch((error) => {
          console.error('Error initializing journey:', error);
        });
    }
    // empty out journey to avoid flicker if the user loads up another journey
    return () => {
      resetEditor();
      resetNeueEditor();
      resetTalkToJourney();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, isLandingDemo]);

  useEffect(() => {
    if (isDynamicFontsReady) {
      appendDynamicFont('headings', currentDynamicFont.headings);
      appendDynamicFont('paragraphs', currentDynamicFont.paragraphs);
    }
  }, [isDynamicFontsReady, currentDynamicFont]);

  useEffect(() => {
    // Inject CSS to make editor responsive from 200px to 1000px
    const style = document.createElement('style');
    style.id = 'mobile-editor-responsive-styles';
    style.textContent = `
      /* Make editor content responsive based on screen size */
      .mobile-editor-wrapper {
        width: 100%;
        height: 100%;
      }
      
      /* Ensure right toolbar is always visible */
      .mobile-editor-wrapper [class*="z-neue-new-block-button-bar"] {
        display: flex !important;
        visibility: visible !important;
        pointer-events: auto !important;
      }
      
      /* Very narrow screens (200px - 400px) */
      @media (min-width: 200px) and (max-width: 400px) {
        .mobile-editor-wrapper [class*="relative"][class*="w-full"][class*="h-full"][class*="overflow-y-auto"] {
          padding-right: 72px !important; /* Space for right toolbar */
        }
        
        .mobile-editor-wrapper [class*="shrink-0"][class*="transition"] {
          display: none !important;
        }
        
        .mobile-editor-wrapper [class*="shrink-0"][class*="w-6"] {
          display: none !important;
        }
        
        .mobile-editor-wrapper [class*="flex"][class*="mx-auto"][class*="relative"] {
          width: 100% !important;
          min-width: 0 !important;
          max-width: calc(100% - 72px) !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          padding-left: 0.25rem !important;
          padding-right: 0.25rem !important;
        }
      }
      
      /* Medium screens (400px - 600px) */
      @media (min-width: 401px) and (max-width: 600px) {
        .mobile-editor-wrapper [class*="relative"][class*="w-full"][class*="h-full"][class*="overflow-y-auto"] {
          padding-right: 80px !important;
        }
        
        .mobile-editor-wrapper [class*="shrink-0"][class*="transition"] {
          display: none !important;
        }
        
        .mobile-editor-wrapper [class*="shrink-0"][class*="w-6"] {
          display: none !important;
        }
        
        .mobile-editor-wrapper [class*="flex"][class*="mx-auto"][class*="relative"] {
          width: 100% !important;
          min-width: 0 !important;
          max-width: calc(100% - 80px) !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          padding-left: 0.75rem !important;
          padding-right: 0.75rem !important;
        }
      }
      
      /* Wider screens (600px - 1000px) */
      @media (min-width: 601px) and (max-width: 1000px) {
        .mobile-editor-wrapper [class*="relative"][class*="w-full"][class*="h-full"][class*="overflow-y-auto"] {
          padding-right: 80px !important;
        }
        
        .mobile-editor-wrapper [class*="shrink-0"][class*="transition"] {
          display: none !important;
        }
        
        .mobile-editor-wrapper [class*="shrink-0"][class*="w-6"] {
          display: none !important;
        }
        
        .mobile-editor-wrapper [class*="flex"][class*="mx-auto"][class*="relative"] {
          width: 100% !important;
          min-width: 0 !important;
          max-width: calc(100% - 80px) !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
          padding-left: 1rem !important;
          padding-right: 1rem !important;
        }
      }
      
      /* Above 1000px: use original responsive behavior */
      @media (min-width: 1001px) {
        .mobile-editor-wrapper [class*="flex"][class*="mx-auto"][class*="relative"] {
          width: 66.666667% !important;
          min-width: 720px !important;
          max-width: 1440px !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    // Also use JavaScript to directly manipulate DOM elements for better reliability
    const updateEditorWidth = () => {
      const wrapper = document.querySelector('.mobile-editor-wrapper');
      if (!wrapper) return;
      
      const width = window.innerWidth;
      const isResponsiveRange = width >= 200 && width <= 1000;
      
      // Find the content div (the one with flex mx-auto relative classes)
      const contentDivs = wrapper.querySelectorAll('div');
      contentDivs.forEach((div) => {
        const classes = div.className || '';
        
        // Ensure right toolbar is visible
        if (classes.includes('z-neue-new-block-button-bar') || classes.includes('neue-new-block-button-bar')) {
          div.style.display = 'flex';
          div.style.visibility = 'visible';
          div.style.pointerEvents = 'auto';
        }
        
        // Check if this is the main content area
        if (classes.includes('flex') && classes.includes('mx-auto') && classes.includes('relative')) {
          if (isResponsiveRange) {
            // Calculate available width (accounting for toolbar ~80px)
            const toolbarWidth = 80;
            const availableWidth = width - toolbarWidth;
            
            div.style.width = '100%';
            div.style.minWidth = '0';
            div.style.maxWidth = `${availableWidth}px`;
            div.style.marginLeft = '0';
            div.style.marginRight = '0';
            
            // Adjust padding based on screen width
            if (width <= 400) {
              div.style.paddingLeft = '0.25rem';
              div.style.paddingRight = '0.25rem';
            } else if (width <= 600) {
              div.style.paddingLeft = '0.75rem';
              div.style.paddingRight = '0.75rem';
            } else {
              div.style.paddingLeft = '1rem';
              div.style.paddingRight = '1rem';
            }
          } else if (width > 1000) {
            // Desktop: restore original behavior
            div.style.width = '';
            div.style.minWidth = '';
            div.style.maxWidth = '';
            div.style.marginLeft = '';
            div.style.marginRight = '';
            div.style.paddingLeft = '';
            div.style.paddingRight = '';
          }
        }
        
        // Hide left sidebar in responsive range
        if (isResponsiveRange && classes.includes('shrink-0') && classes.includes('transition')) {
          div.style.display = 'none';
        } else if (width > 1000 && classes.includes('shrink-0') && classes.includes('transition')) {
          div.style.display = '';
        }
        
        // Hide right spacer in responsive range
        if (isResponsiveRange && classes.includes('shrink-0') && classes.includes('w-6')) {
          div.style.display = 'none';
        } else if (width > 1000 && classes.includes('shrink-0') && classes.includes('w-6')) {
          div.style.display = '';
        }
      });
      
      // Also update the editor content container padding
      const editorContentContainer = wrapper.querySelector('[class*="relative"][class*="w-full"][class*="h-full"][class*="overflow-y-auto"]');
      if (editorContentContainer && isResponsiveRange) {
        (editorContentContainer as HTMLElement).style.paddingRight = '80px';
      } else if (editorContentContainer && width > 1000) {
        (editorContentContainer as HTMLElement).style.paddingRight = '';
      }
    };
    
    // Update on mount and resize
    const timeoutId = setTimeout(updateEditorWidth, 100);
    window.addEventListener('resize', updateEditorWidth);
    
    // Also observe for DOM changes (editor might load asynchronously)
    const observer = new MutationObserver(() => {
      updateEditorWidth();
    });
    
    const wrapper = document.querySelector('.mobile-editor-wrapper');
    if (wrapper) {
      observer.observe(wrapper, { childList: true, subtree: true });
    }
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateEditorWidth);
      observer.disconnect();
      const existingStyle = document.getElementById('mobile-editor-responsive-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, []);

  if (!journey) {
    return null;
  }

  return (
    <div className="mobile-editor-wrapper w-full h-full">
      <ContentCreationEditor key={uuid} />
      <JoyrideOnboardingGuide initial={true} journey={journey} />
    </div>
  );
};
